import { useRouter } from "vue-router";
import useAuth from "../composables/useAuth";
import useFedid from '../composables/useFedid';
import useLanguage from "@/modules/language/composables/useLanguage";
import i18nConfig from "@/modules/language/configuration/i18nConfig";
import { Data, LanguageTypes } from "../interfaces/ResponseUserInfo";
import { AxiosError, isAxiosError } from "axios";
import { jwtDecode } from "jwt-decode";
import { ROLE_APPS } from "../constants/roles";

const fedidService = {
    refreshTokenAndSaveData: async (checkLastDate: boolean): Promise<void> => {
        const {
            isTranscurredRefreshTime,
            setAccessToken,
            setInitDate,
            setRefreshToken,
            revokeSession,
            refreshToken
        } = useAuth();

        const { onRefreshToken } = useFedid();

        if ((isTranscurredRefreshTime && checkLastDate) || !checkLastDate) {

            if(!refreshToken.value){
                revokeSession();
                globalThis.location.href = globalThis.location.origin;
                return;
            }
            try {
                const { data, status } = await onRefreshToken(refreshToken.value);

                if ( status === 200) {
                    setInitDate();
                    setAccessToken(data.accessToken);
                    setRefreshToken(data.refreshToken);
                } else {
                    revokeSession();
                    globalThis.location.href = globalThis.location.origin;
                }
            } catch {
                revokeSession();
                globalThis.location.href = globalThis.location.origin;
            }
        }
    },
    getTokenAndSaveData: async (): Promise<void> => {
        const router = useRouter();
        const {
            accessToken,
            loginState,
            refreshToken,
            revokeSession,
            setAccessToken,
            setInitDate,
            setLastTokenDate,
            setLoginState,
            setRefreshToken,
            setUserInfo,
            getRolesByAppName,
        } = useAuth();

        const { redirectFedid } = useFedid();
        const { changeLanguage } = useLanguage()

        if (loginState.value && accessToken.value) {
            return;
        }

        if (loginState.value && !accessToken.value) {
            if (refreshToken.value) {
                await fedidService.refreshTokenAndSaveData(false);
                if (accessToken.value) {
                    return;
                }
            }
            revokeSession();
        }

        const response = await (async () => {
            try {
                return await redirectFedid();
            } catch (err) {
                if (isAxiosError(err) && (err.response?.status === 401 || err.response?.status === 403)) {
                    router.replace({ name: "noRole" });
                    return undefined;
                }
                throw err;
            }
        })();
        if (!response) return;
        const { data, status } = response;
        if (status !== 200) return;

        const userInfo: Data = jwtDecode(data.idToken);
        setUserInfo(userInfo);
        setLoginState(true);
        setInitDate();
        setLastTokenDate();
        setAccessToken(data.accessToken);
        setRefreshToken(data.refreshToken);

        const language = userInfo.preferredLanguage as LanguageTypes;
        changeLanguage(language);
        i18nConfig.global.locale.value = language;

        const dassistRoles = getRolesByAppName(ROLE_APPS.DASSIST_ROLES) ?? [];
        const hdaRoles = getRolesByAppName(ROLE_APPS.HDA_ROLES) ?? [];

        if (dassistRoles.length > 0) {
            router.replace({ name: "visitors" });
        } else if (hdaRoles.length > 0) {
            router.replace({ name: "collaborator" });
        } else {
            router.replace({ name: "noRole" });
        }
    },
    revokeSession: (): void => {
        const { lastTokenDate, revokeSession } = useAuth();
        if (!lastTokenDate.value) return;
        if ((Date.now() - new Date(lastTokenDate.value).getTime()) / 1000 / 60 > process.env.VUE_APP_LOGIN_REVOKED_TIME) {
            revokeSession();
            globalThis.location.reload();
        }
    },
    logout: () => {
        const { revokeSession, userInfo } = useAuth();
        const { revokeFed } = useFedid();
        if (!userInfo.value) return;
        revokeFed(userInfo.value["pi.sri"].split(".")[0]).then(() => {
            revokeSession();
            globalThis.location.reload();
        }).catch((error: AxiosError) => {
            throw new Error(error.message);
        })
    }
}

export default fedidService;
